// src/components/ProjectManagement/TaskAssignmentPanel.jsx
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

// Searchable Worker Dropdown Component - USING PORTAL FOR RELIABLE POSITIONING
const SearchableWorkerDropdown = ({ workers, selectedWorkerId, onSelect, placeholder }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [filteredWorkers, setFilteredWorkers] = useState(workers);
  const [dropdownStyle, setDropdownStyle] = useState({});
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredWorkers(workers);
    } else {
      const term = searchTerm.toLowerCase();
      const filtered = workers.filter(worker => 
        worker.name?.toLowerCase().includes(term) ||
        worker.role?.toLowerCase().includes(term) ||
        worker.email?.toLowerCase().includes(term)
      );
      setFilteredWorkers(filtered);
    }
  }, [searchTerm, workers]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          buttonRef.current && !buttonRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (workerId) => {
    onSelect(workerId);
    setSearchTerm('');
    setIsOpen(false);
  };

  const selectedWorker = workers.find(w => w.id === selectedWorkerId);

  const toggleDropdown = () => {
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownStyle({
        position: 'fixed',
        top: rect.bottom + window.scrollY + 5,
        left: rect.left + window.scrollX,
        width: rect.width,
        zIndex: 10000
      });
    }
    setIsOpen(!isOpen);
  };

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <div 
        ref={buttonRef}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          border: '1px solid #ddd',
          borderRadius: '8px',
          padding: '10px 12px',
          background: 'white',
          cursor: 'pointer',
          minHeight: '42px',
          backgroundColor: '#fff'
        }}
        onClick={toggleDropdown}
      >
        <span style={{ color: selectedWorkerId ? '#333' : '#999', fontSize: '14px' }}>
          {selectedWorkerId ? selectedWorker?.name || 'Select worker...' : placeholder || 'Search or select worker...'}
        </span>
        <span style={{ color: '#999', fontSize: '12px' }}>{isOpen ? '▲' : '▼'}</span>
      </div>
      
      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          style={{
            ...dropdownStyle,
            background: 'white',
            border: '1px solid #ddd',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            maxHeight: '300px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <div style={{ padding: '10px 12px', borderBottom: '1px solid #eee' }}>
            <input
              type="text"
              placeholder="Search by name or role..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '13px',
                outline: 'none'
              }}
              autoFocus
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <div style={{ maxHeight: '240px', overflowY: 'auto' }}>
            {filteredWorkers.length === 0 ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                No workers found
              </div>
            ) : (
              filteredWorkers.map(worker => (
                <div
                  key={worker.id}
                  onClick={() => handleSelect(worker.id)}
                  style={{
                    padding: '10px 12px',
                    cursor: 'pointer',
                    borderBottom: '1px solid #f5f5f5',
                    transition: 'background 0.2s ease'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                >
                  <div style={{ fontWeight: '500', fontSize: '14px' }}>{worker.name}</div>
                  <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                    {worker.role?.replace('-', ' ')} 
                    {worker.email && <span style={{ marginLeft: '8px' }}>✉️ {worker.email}</span>}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

// Searchable Multi-Worker Selector Component - USING PORTAL
const SearchableMultiWorkerSelector = ({ workers, selectedWorkerIds, onToggle, onConfirm }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [tempSelected, setTempSelected] = useState([...selectedWorkerIds]);
  const [dropdownStyle, setDropdownStyle] = useState({});
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);

  const filteredWorkers = searchTerm.trim() === '' 
    ? workers 
    : workers.filter(worker => 
        worker.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        worker.role?.toLowerCase().includes(searchTerm.toLowerCase())
      );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          buttonRef.current && !buttonRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = (workerId) => {
    if (tempSelected.includes(workerId)) {
      setTempSelected(tempSelected.filter(id => id !== workerId));
    } else {
      setTempSelected([...tempSelected, workerId]);
    }
  };

  const handleConfirm = () => {
    onConfirm(tempSelected);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleCancel = () => {
    setTempSelected([...selectedWorkerIds]);
    setIsOpen(false);
    setSearchTerm('');
  };

  const openDropdown = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownStyle({
        position: 'fixed',
        top: rect.bottom + window.scrollY + 5,
        left: rect.left + window.scrollX,
        width: rect.width,
        zIndex: 10000
      });
    }
    setIsOpen(true);
  };

  const selectedCount = selectedWorkerIds.length;

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <div 
        ref={buttonRef}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          border: '1px solid #ddd',
          borderRadius: '8px',
          padding: '10px 12px',
          background: 'white',
          cursor: 'pointer',
          minHeight: '42px',
          backgroundColor: '#fff'
        }}
        onClick={openDropdown}
      >
        <span style={{ color: selectedCount > 0 ? '#333' : '#999', fontSize: '14px' }}>
          {selectedCount > 0 
            ? `${selectedCount} worker${selectedCount !== 1 ? 's' : ''} selected` 
            : 'Search or select workers...'}
        </span>
        <span style={{ color: '#999', fontSize: '12px' }}>▼</span>
      </div>
      
      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          style={{
            ...dropdownStyle,
            background: 'white',
            border: '1px solid #ddd',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            maxHeight: '400px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <div style={{ padding: '12px', borderBottom: '1px solid #eee' }}>
            <input
              type="text"
              placeholder="Search by name or role..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '13px',
                outline: 'none'
              }}
              autoFocus
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <div style={{ maxHeight: '280px', overflowY: 'auto' }}>
            {filteredWorkers.length === 0 ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                No workers found
              </div>
            ) : (
              filteredWorkers.map(worker => (
                <label
                  key={worker.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '10px 12px',
                    cursor: 'pointer',
                    borderBottom: '1px solid #f5f5f5',
                    transition: 'background 0.2s ease'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                >
                  <input
                    type="checkbox"
                    checked={tempSelected.includes(worker.id)}
                    onChange={() => handleToggle(worker.id)}
                    style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '500', fontSize: '14px' }}>{worker.name}</div>
                    <div style={{ fontSize: '11px', color: '#666' }}>{worker.role?.replace('-', ' ')}</div>
                  </div>
                </label>
              ))
            )}
          </div>
          <div style={{ 
            padding: '12px', 
            borderTop: '1px solid #eee',
            display: 'flex',
            gap: '10px'
          }}>
            <button 
              onClick={handleConfirm}
              style={{
                flex: 1,
                padding: '8px',
                background: '#800020',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '500'
              }}
            >
              Confirm ({tempSelected.length})
            </button>
            <button 
              onClick={handleCancel}
              style={{
                flex: 1,
                padding: '8px',
                background: '#f3f4f6',
                color: '#374151',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

const TaskAssignmentPanel = ({ tasks, workers, onAssignTask, onAssignAll, projectStartDate, projectEndDate }) => {
  const [assignments, setAssignments] = useState({});
  const [selectedWorkersForTask, setSelectedWorkersForTask] = useState({});
  const [isMultipleMode, setIsMultipleMode] = useState({});

  useEffect(() => {
    setAssignments({});
    setSelectedWorkersForTask({});
    setIsMultipleMode({});
  }, [tasks]);

  const handleAssignTask = (taskId, workerIds, task) => {
    const workerIdsArray = Array.isArray(workerIds) ? workerIds : [workerIds];
    setAssignments({ ...assignments, [taskId]: workerIdsArray });
    setSelectedWorkersForTask({ ...selectedWorkersForTask, [taskId]: workerIdsArray });
    
    const workerNames = workerIdsArray.map(id => {
      const worker = workers.find(w => w.id === id);
      return worker?.name || 'Unknown';
    });
    
    onAssignTask(taskId, workerIdsArray, { ...task, assignedToNames: workerNames });
  };

  const toggleWorkerForTask = (taskId, workerId) => {
    const current = selectedWorkersForTask[taskId] || [];
    let newSelection;
    if (current.includes(workerId)) {
      newSelection = current.filter(id => id !== workerId);
    } else {
      newSelection = [...current, workerId];
    }
    setSelectedWorkersForTask({ ...selectedWorkersForTask, [taskId]: newSelection });
  };

  const confirmAssignment = (taskId, task) => {
    const selected = selectedWorkersForTask[taskId] || [];
    if (selected.length === 0) {
      alert('Please select at least one worker');
      return;
    }
    handleAssignTask(taskId, selected, task);
  };

  const isFullyAssigned = () => {
    return tasks.length > 0 && Object.keys(assignments).length === tasks.length;
  };

  const getWorkerNames = (workerIds) => {
    if (!workerIds) return '';
    const ids = Array.isArray(workerIds) ? workerIds : [workerIds];
    return ids.map(id => {
      const worker = workers.find(w => w.id === id);
      return worker?.name || 'Unknown';
    }).join(', ');
  };

  const formatDate = (date) => {
    if (!date) return 'Not set';
    if (date.toDate) return date.toDate().toLocaleDateString();
    return new Date(date).toLocaleDateString();
  };

  if (tasks.length === 0) {
    return <div className="task-assignment-panel"><div className="empty-message">No tasks to assign. Please add products or custom tasks first.</div></div>;
  }

  return (
    <div className="task-assignment-panel">
      <div className="assignment-header">
        <h3>📋 Assign Tasks to Workers</h3>
        <p>All tasks must be assigned before creating the project</p>
        <div className="assignment-progress">
          <div className="progress-info">
            <span>Assigned: {Object.keys(assignments).length} / {tasks.length} tasks</span>
            <span className={`status-badge ${isFullyAssigned() ? 'complete' : 'pending'}`}>
              {isFullyAssigned() ? 'Ready to Create' : 'Pending Assignment'}
            </span>
          </div>
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${(Object.keys(assignments).length / tasks.length) * 100}%` }}></div>
          </div>
        </div>
      </div>

      <div className="tasks-to-assign" style={{ maxHeight: '50vh', overflowY: 'auto', paddingRight: '8px' }}>
        {tasks.map(task => {
          const isAssigned = !!assignments[task.id];
          const selectedForTask = selectedWorkersForTask[task.id] || [];
          const isMultiMode = isMultipleMode[task.id] || false;
          
          return (
            <div key={task.id} className={`task-assignment-item ${isAssigned ? 'assigned' : ''}`} style={{ marginBottom: '20px', paddingBottom: '16px', borderBottom: '1px solid #eee' }}>
              <div className="task-info" style={{ marginBottom: '12px' }}>
                <div className="task-name" style={{ fontWeight: '600', marginBottom: '6px' }}>{task.name}</div>
                <div className="task-dates" style={{ display: 'flex', gap: '16px', fontSize: '12px', color: '#666' }}>
                  <span>📅 {formatDate(task.startDate)} → {formatDate(task.endDate)}</span>
                  <span className="task-duration">⏱️ {task.days} days</span>
                </div>
                {task.productName && <div className="task-product" style={{ fontSize: '11px', color: '#800020', marginTop: '4px' }}>📦 From: {task.productName}</div>}
              </div>
              
              <div className="assignment-control">
                {isAssigned ? (
                  <div className="assigned-info" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                    <span className="assigned-worker" style={{ fontSize: '13px', color: '#065f46', background: '#d1fae5', padding: '6px 12px', borderRadius: '8px' }}>
                      👥 {getWorkerNames(assignments[task.id])}
                      {Array.isArray(assignments[task.id]) && assignments[task.id].length > 1 && (
                        <span className="multi-badge" style={{ marginLeft: '6px', background: '#800020', color: 'white', padding: '2px 6px', borderRadius: '10px', fontSize: '10px' }}>{assignments[task.id].length} workers</span>
                      )}
                    </span>
                    <button className="reassign-btn" onClick={() => {
                      setAssignments({ ...assignments, [task.id]: undefined });
                      setSelectedWorkersForTask({ ...selectedWorkersForTask, [task.id]: [] });
                    }} style={{ padding: '6px 12px', background: '#f3f4f6', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
                      Change
                    </button>
                  </div>
                ) : (
                  <div className="assignment-input">
                    <div className="mode-toggle-small" style={{ display: 'flex', gap: '10px', marginBottom: '12px' }}>
                      <button 
                        className={`small-mode-btn ${!isMultiMode ? 'active' : ''}`}
                        onClick={() => setIsMultipleMode({ ...isMultipleMode, [task.id]: false })}
                        style={{
                          flex: 1,
                          padding: '8px 12px',
                          borderRadius: '8px',
                          border: '1px solid #ddd',
                          background: !isMultiMode ? '#800020' : 'white',
                          color: !isMultiMode ? 'white' : '#333',
                          cursor: 'pointer',
                          fontWeight: '500'
                        }}
                      >
                        Single Worker
                      </button>
                      <button 
                        className={`small-mode-btn ${isMultiMode ? 'active' : ''}`}
                        onClick={() => setIsMultipleMode({ ...isMultipleMode, [task.id]: true })}
                        style={{
                          flex: 1,
                          padding: '8px 12px',
                          borderRadius: '8px',
                          border: '1px solid #ddd',
                          background: isMultiMode ? '#800020' : 'white',
                          color: isMultiMode ? 'white' : '#333',
                          cursor: 'pointer',
                          fontWeight: '500'
                        }}
                      >
                        Multiple Workers
                      </button>
                    </div>
                    
                    {!isMultiMode ? (
                      <SearchableWorkerDropdown 
                        workers={workers}
                        selectedWorkerId={selectedForTask[0] || ''}
                        onSelect={(workerId) => setSelectedWorkersForTask({ ...selectedWorkersForTask, [task.id]: [workerId] })}
                        placeholder="Search or select worker..."
                      />
                    ) : (
                      <SearchableMultiWorkerSelector 
                        workers={workers}
                        selectedWorkerIds={selectedForTask}
                        onToggle={(workerId) => toggleWorkerForTask(task.id, workerId)}
                        onConfirm={(selectedIds) => {
                          setSelectedWorkersForTask({ ...selectedWorkersForTask, [task.id]: selectedIds });
                        }}
                      />
                    )}
                    
                    <button 
                      className="confirm-assign-btn"
                      onClick={() => confirmAssignment(task.id, task)}
                      disabled={selectedForTask.length === 0}
                      style={{
                        marginTop: '12px',
                        width: '100%',
                        padding: '10px',
                        background: selectedForTask.length === 0 ? '#ccc' : '#800020',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: selectedForTask.length === 0 ? 'not-allowed' : 'pointer',
                        fontWeight: '600'
                      }}
                    >
                      ✓ Assign Task
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {!isFullyAssigned() && tasks.length > 0 && (
        <div className="assignment-warning" style={{ marginTop: '20px', padding: '12px', background: '#fef3c7', borderRadius: '10px', textAlign: 'center' }}>
          ⚠️ Please assign all {tasks.length} tasks before creating the project
        </div>
      )}

      {isFullyAssigned() && (
        <div className="assignment-success" style={{ marginTop: '20px', padding: '12px', background: '#d1fae5', borderRadius: '10px', textAlign: 'center' }}>
          ✅ All {tasks.length} tasks have been assigned! You can now create the project.
        </div>
      )}
    </div>
  );
};

export default TaskAssignmentPanel;